import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Button, Table, TableBody, TableCell, TableHead, TableRow, Paper, Typography, Box, TextField, IconButton, MenuItem, Select, FormControl, } from '@mui/material';
import { DataContext, FileData } from '../../context/DataContext';
import { SettingsContext } from '../../context/SettingsContext';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TradingMetricsCalculator from '../../helpers/TradingMetricsCalculator';
import { LoadingContext } from '../../context/LoadingContext'; 
import { useDropzone } from 'react-dropzone';
import { useTranslation } from '../../context/TranslationContext';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddDatasetDialog from './components/AddDatasetDialog';
import ManageDatasetsDialog from './components/SelectDatasetDialog';
import ConfirmDialog from './components/ConfirmDialog';
import ConfigDialog from './components/ConfigDialog';
import InitialCapitalDialog from './components/InitialCapitalDialog';
import { CSVParser, ParsedConfigData } from '../../helpers/CSVParser';
import ColoredSwitch from '../../components/ColoredSwitch';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { Dayjs } from 'dayjs';
import ClearIcon from '@mui/icons-material/Clear';

const calculateTradeDatesAndCounts = (files: FileData[], includedOnly: boolean = false) => {
  let firstTradeDate: Date | undefined;
  let lastTradeDate: Date | undefined;
  let tradeCount = 0;

  files.forEach(file => {
    if (!includedOnly || file.include) {
      const fileFirstTradeDate = new Date(file.parsedData[0]?.datetime);
      const fileLastTradeDate = new Date(file.parsedData[file.parsedData.length - 1]?.datetime);

      if (!firstTradeDate || fileFirstTradeDate < firstTradeDate) {
        firstTradeDate = fileFirstTradeDate;
      }
      if (!lastTradeDate || fileLastTradeDate > lastTradeDate) {
        lastTradeDate = fileLastTradeDate;
      }
      tradeCount += file.parsedData.length;
    }
  });

  return { firstTradeDate, lastTradeDate, tradeCount };
};

const ManageDataPage = () => {
  const { dataset, addFiles, removeFile, updateFiles, clearFiles, datasets, addDataset, removeDataset, setActiveDataset, activeDatasetId, updateDatasets } = useContext(DataContext);
  const { showFileInfos, toggleShowFileInfos, getLocale } = useContext(SettingsContext);
  const { setLoading } = useContext(LoadingContext);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newId, setNewId] = useState<string>('');
  const [newInitialCapital, setNewInitialCapital] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [recalculatedAmounts, setRecalculatedAmounts] = useState<{ [key: string]: number }>({});
  const [configParentFileId, setConfigParentFileId] = useState<string | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectDatasetDialogOpen, setSelectDatasetDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<any>(null);
  const [deleteConfigDialogOpen, setDeleteConfigDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<string | null>(null);
  const [newDatasetName, setNewDatasetName] = useState<string>('');
  const [editingDatasetId, setEditingDatasetId] = useState<string | null>(null);
  const [editedDatasetName, setEditedDatasetName] = useState<string>('');
  const [addDatasetDialogOpen, setAddDatasetDialogOpen] = useState(false);
  const [initialCapitalDialogOpen, setInitialCapitalDialogOpen] = useState(false);
  const [initialCapitalFromConfig, setInitialCapitalFromConfig] = useState<number | null>(null);
  const [confirmDatasetDeleteDialogOpen, setConfirmDatasetDeleteDialogOpen] = useState(false);
  const [datasetToDelete, setDatasetToDelete] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const tableFontSize = '1rem';
  const { t } = useTranslation();

  useEffect(() => {
    setLoading(false);
  }, [setLoading]);
  
  useEffect(() => {
    const newRecalculatedAmounts = dataset.files.reduce((acc, file) => {
      acc[file.customId] = TradingMetricsCalculator.calculateInitialCapital(file.parsedData);
      return acc;
    }, {} as { [key: string]: number });
    setRecalculatedAmounts(newRecalculatedAmounts);
  }, [dataset]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const newFiles: FileData[] = [];
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      if (!file || file.type !== 'text/csv') { // Add this check
        alert('Only CSV files are allowed!');
        return;
      }
      const reader = new FileReader();
      reader.onload = async (e) => {
        const content = e.target?.result as string;
        try {
          const parsedContent = CSVParser.parse(content);
          const initialCapital = TradingMetricsCalculator.calculateInitialCapital(parsedContent);
          const uniqueFile: FileData = {
            id: Date.now(),
            customId: `${file.name}-${Date.now()}`, // Ensure unique ID
            name: file.name,
            lines: content.split('\n').length,
            content: content,
            include: true, // Default to include
            initialCapital: initialCapital,
            parsedData: parsedContent, 
            timestamp: Date.now() // Add timestamp
          };
          newFiles.push(uniqueFile);
          if (newFiles.length === acceptedFiles.length) {
            await addFiles(newFiles);
          }
        } catch (error) {
          const errorMessage = (error instanceof Error) ? error.message : 'Unknown error';
          alert(`Import error occurred with file ${file.name}: ${errorMessage}`);
          return;
        }
      };
      reader.readAsText(file);
    }
  }, [addFiles]);

  const handleConfigFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const _files = event.target.files;
    if (!_files || _files.length === 0 || !configParentFileId) return;
  
    const file = _files[0];
    if (file.type !== 'text/csv') {
      alert('Only CSV files are allowed!');
      return;
    }
  
    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target?.result as string;
      const parsedConfig: ParsedConfigData = CSVParser.parseConfig(content);
      const updatedFiles = dataset.files.map(updatedFile => 
        updatedFile.customId === configParentFileId ? { ...updatedFile, parsedConfig, configFileName: file.name } : updatedFile
      );
      updateFiles(updatedFiles);
      if (parsedConfig.tradingView.initialCapital) {
        const numberStr = parsedConfig.tradingView.initialCapital.split(' ')[0];
        setInitialCapitalFromConfig(Number(numberStr));
        setInitialCapitalDialogOpen(true);        
      } else {
        setConfigParentFileId(null);
      }
    };
    reader.readAsText(file);
    // setConfigParentFileId(null);
  };

  const handleConfirmInitialCapital = () => {
    if (configParentFileId && initialCapitalFromConfig !== null) {
      const updatedFiles = dataset.files.map(file => 
        file.customId === configParentFileId ? { ...file, initialCapital: initialCapitalFromConfig } : file
      );
      updateFiles(updatedFiles);
    }
    setInitialCapitalDialogOpen(false);
    setInitialCapitalFromConfig(null);
  };

  const handleCancelInitialCapital = () => {
    setInitialCapitalDialogOpen(false);
    setInitialCapitalFromConfig(null);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: { 'text/csv': ['.csv'] } });

  const handleRemoveFile = (fileId: string) => {
    setFileToDelete(fileId);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (fileToDelete) {
      removeFile(fileToDelete);
    } else {
      clearFiles();
    }
    setConfirmDialogOpen(false);
    setFileToDelete(null);
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setFileToDelete(null);
  };

  const handleEditClick = (fileId: string, currentId: string, initialCapital: number) => {
    setEditingId(fileId);
    setNewId(currentId);
    setNewInitialCapital(initialCapital);
    setError(null);
  };

  const handleSaveClick = (fileId: string) => {
    const trimmedNewId = newId.trim();
    if (!trimmedNewId) {
      setError('ID cannot be empty.');
      return;
    }
    if (dataset.files.some(file => file.customId === trimmedNewId && file.customId !== fileId)) {
      setError('ID already exists. Please choose a different ID.');
      return;
    }
    const updatedFiles = dataset.files.map(file => 
      file.customId === fileId ? { ...file, customId: trimmedNewId, initialCapital: newInitialCapital || 0 } : file
    );
    updateFiles(updatedFiles);
    setEditingId(null);
    setError(null);
  };

  const handleCancelClick = () => {
    setEditingId(null);
    setError(null);
  };

  const handleIncludeChange = (fileId: string) => {
    const updatedFiles = dataset.files.map(file => 
      file.customId === fileId ? { ...file, include: !file.include } : file
    );
    updateFiles(updatedFiles);
  };

  const moveFile = (index: number, direction: 'up' | 'down') => {
    const newFiles = [...dataset.files];
    const [movedFile] = newFiles.splice(index, 1);
    newFiles.splice(direction === 'up' ? index - 1 : index + 1, 0, movedFile);
    updateFiles(newFiles);
  };

  const formatValue = (value: number, decimals: number) => {
    return value ? value.toLocaleString(getLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : 1;
  };

  const translateConfigKeys = (config: ParsedConfigData) => {
    if (!config) return null; // Add this check
    const translatedConfig: { [key: string]: { [key: string]: string } } = { tradingView: {}, indicator: {} };
    for (const key of Object.keys(config['tradingView']) as Array<keyof typeof config['tradingView']>) {
      translatedConfig['tradingView'][t(key)] = config['tradingView'][key];
    }
    for (const key of Object.keys(config['indicator']) ) {
      translatedConfig['indicator'][key] = config['indicator'][key];
    }
    return translatedConfig;
  };

  const handleViewConfig = (config: ParsedConfigData) => {
    const translatedConfig = translateConfigKeys(config);
    if (translatedConfig) {
      setSelectedConfig(translatedConfig);
      setConfigDialogOpen(true);
    } else {
      alert(t("Config data is missing or invalid."));
    }
  };

  const handleCloseConfigDialog = () => {
    setConfigDialogOpen(false);
    setSelectedConfig(null);
  };

  const handleDeleteConfig = (fileId: string) => {
    setConfigToDelete(fileId);
    setDeleteConfigDialogOpen(true);
  };

  const handleConfirmDeleteConfig = () => {
      if (configToDelete) {
        const updatedFiles = dataset.files.map(file => 
          file.customId === configToDelete ? { ...file, parsedConfig: undefined, configFileName: undefined } : file
        );
        updateFiles(updatedFiles);
      }
      setDeleteConfigDialogOpen(false);
      setConfigToDelete(null);
    };

  const handleCancelDeleteConfig = () => {
    setDeleteConfigDialogOpen(false);
    setConfigToDelete(null);
  };

  const handleAddDataset = () => {
    const trimmedName = newDatasetName.trim();
    if (trimmedName.length > 30) {
      alert(t("Dataset name cannot be longer than 30 characters."));
      return;
    }
    if (trimmedName) {
      addDataset(trimmedName);
      setNewDatasetName('');
      setAddDatasetDialogOpen(false);
    }
  };

  const handleRemoveDataset = (datasetId: string) => {
    if (datasets && datasets.length > 1) {
      setDatasetToDelete(datasetId);
      setConfirmDatasetDeleteDialogOpen(true);
    } else {
      alert(t("Cannot delete the last dataset."));
    }
  };

  const handleConfirmDeleteDataset = () => {
    if (datasetToDelete) {
      removeDataset(datasetToDelete);
    }
    setConfirmDatasetDeleteDialogOpen(false);
    setDatasetToDelete(null);
  };

  const handleCancelDeleteDataset = () => {
    setConfirmDatasetDeleteDialogOpen(false);
    setDatasetToDelete(null);
  };

  const handleSetActiveDataset = (datasetId: string) => {
    setActiveDataset(datasetId);
  };

  const handleEditDatasetName = (datasetId: string, name: string) => {
    setEditingDatasetId(datasetId);
    setEditedDatasetName(name);
  };

  const handleSaveDatasetName = (datasetId: string) => {
    const trimmedName = editedDatasetName.trim();
    if (!trimmedName) {
      alert(t("Dataset name cannot be empty."));
      return;
    }
    const updatedDatasets = datasets?.map(dataset => 
      dataset.id === datasetId ? { ...dataset, name: trimmedName } : dataset
    );
    if (updatedDatasets) {
      updateDatasets(updatedDatasets);
    }
    setEditingDatasetId(null);
    setEditedDatasetName('');
  };

  const handleCancelEditDatasetName = () => {
    setEditingDatasetId(null);
    setEditedDatasetName('');
  };

  const handleOpenAddDatasetDialog = () => {
    setAddDatasetDialogOpen(true);
  };

  const handleCloseAddDatasetDialog = () => {
    setAddDatasetDialogOpen(false);
    setNewDatasetName('');
  };

  const handleOpenSelectDatasetDialog = () => {
    setSelectDatasetDialogOpen(true);
  };

  const handleCloseSelectDatasetDialog = () => {
    setSelectDatasetDialogOpen(false);
  };

  const trimAndSetEditedDatasetName = (name: string) => {
    // dont allow initial spaces
    while (name.startsWith(' ')) {
      name = name.substring(1);
    }
    setEditedDatasetName(name.length > 30 ? name.substring(0, 30) : name);
  }

  const handleKeyPress = (event: React.KeyboardEvent, saveFunction: () => void, cancelFunction?: () => void) => {
    if (event.key === 'Enter') {
      saveFunction();
    } else if (event.key === 'Escape' && cancelFunction) {
      cancelFunction();
    }
  };

  const handleUseDeepBacktestChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedDatasets = datasets?.map(dataset => 
      dataset.id === activeDatasetId ? { ...dataset, useDeepBacktest: event.target.checked } : dataset
    );
    if (updatedDatasets) {
      updateDatasets(updatedDatasets);
    }
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    if (date && date.isAfter(dayjs())) {
      alert(t("Start date cannot be later than today."));
      return;
    }
    const updatedDatasets = datasets?.map(dataset => 
      dataset.id === activeDatasetId ? { ...dataset, customStartDate: date?.toDate() || null } : dataset
    );
    if (updatedDatasets) {
      updateDatasets(updatedDatasets);
    }
  };
  
  const handleEndDateChange = (date: Dayjs | null) => {
    if (date && date.isAfter(dayjs())) {
      alert(t("End date cannot be later than today."));
      return;
    }
    // add 23:59:59 to the date to include the entire day
    if (date) {
      date = date.hour(23).minute(59).second(59).millisecond(999);
    }
    const updatedDatasets = datasets?.map(dataset => 
      dataset.id === activeDatasetId ? { ...dataset, customEndDate: date?.toDate() || null } : dataset
    );
    if (updatedDatasets) {
      updateDatasets(updatedDatasets);
    }
  };

  const handleClearStartDate = () => {
    const updatedDatasets = datasets?.map(dataset => 
      dataset.id === activeDatasetId ? { ...dataset, customStartDate: null } : dataset
    );
    if (updatedDatasets) {
      updateDatasets(updatedDatasets);
    }
  };

  const handleClearEndDate = () => {
    const updatedDatasets = datasets?.map(dataset => 
      dataset.id === activeDatasetId ? { ...dataset, customEndDate: null } : dataset
    );
    if (updatedDatasets) {
      updateDatasets(updatedDatasets);
    }
  };

  const handleRowClick = (fileId: string) => {
    setExpandedRow(expandedRow === fileId ? null : fileId);
  };

  const allFilesStats = calculateTradeDatesAndCounts(dataset.files);
  const includedFilesStats = calculateTradeDatesAndCounts(dataset.files, true);

  const getDatePickerFormat = () => {
    const locale = getLocale();
    switch (locale) {
      case 'en-US': return 'MM/DD/YYYY';
      case 'de-DE': return 'DD.MM.YYYY';
      default: return 'MM/DD/YYYY';
    }
  }

  const getMaxDate = () => {
    return dayjs();
  };

  
  const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };

  return (
      <>
        <header>
        <Box display="flex" justifyContent="space-between" alignItems={"center"} mb={2}>
          <Typography variant="h4" gutterBottom>
            {t("Manage Data")}
          </Typography>
          <Box display="flex" justifyContent="flex-end" alignItems={"center"} >
              <Typography>{t("Active dataset")}:</Typography>
              <FormControl variant="outlined" style={{ marginLeft: '8px', marginRight: '16px' }}>
                <Select
                  value={activeDatasetId || ''}
                  onChange={(e) => handleSetActiveDataset(e.target.value as string)}
                  style={{ height: '38px', width: '400px' }} 
                  >
                  {datasets?.map(dataset => (
                    <MenuItem key={dataset.id} value={dataset.id}>
                      {dataset.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={handleOpenSelectDatasetDialog}>{t("Manage Datasets")}</Button>
          </Box>
        </Box>
        </header>

        <AddDatasetDialog
          open={addDatasetDialogOpen}
          onClose={handleCloseAddDatasetDialog}
          newDatasetName={newDatasetName}
          setNewDatasetName={setNewDatasetName}
          handleAddDataset={handleAddDataset}
          handleKeyPress={handleKeyPress}
        />

        <ManageDatasetsDialog
          open={selectDatasetDialogOpen}
          onClose={handleCloseSelectDatasetDialog}
          datasets={datasets || []}
          activeDatasetId={activeDatasetId}
          handleSetActiveDataset={handleSetActiveDataset}
          editingDatasetId={editingDatasetId}
          editedDatasetName={editedDatasetName}
          handleEditDatasetName={handleEditDatasetName}
          handleSaveDatasetName={handleSaveDatasetName}
          handleCancelEditDatasetName={handleCancelEditDatasetName}
          handleRemoveDataset={handleRemoveDataset}
          handleOpenAddDatasetDialog={handleOpenAddDatasetDialog}
          trimAndSetEditedDatasetName={trimAndSetEditedDatasetName}
          handleKeyPress={handleKeyPress}
        />

        <ConfirmDialog
          open={confirmDialogOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title={t("Confirm Deletion")}
          contentText={t("Are you sure you want to delete") + fileToDelete ? `${t("the file named")}: ${dataset.files.find(file => file.customId === fileToDelete)?.name}` : t("all files")}
        />

        <ConfigDialog
          open={configDialogOpen}
          onClose={handleCloseConfigDialog}
          selectedConfig={selectedConfig}
          t={t}
        />

        <ConfirmDialog
          open={deleteConfigDialogOpen}
          onClose={handleCancelDeleteConfig}
          onConfirm={handleConfirmDeleteConfig}
          title={t("Confirm Config Deletion")}
          contentText={t("Are you sure you want to delete the config file for") + dataset.files.find(file => file.customId === configToDelete)?.name}
        />

        <InitialCapitalDialog
          open={initialCapitalDialogOpen}
          onClose={handleCancelInitialCapital}
          onConfirm={handleConfirmInitialCapital}
          initialCapitalFromConfig={initialCapitalFromConfig ? initialCapitalFromConfig : 0}
          t={t}
        />

        <ConfirmDialog
          open={confirmDatasetDeleteDialogOpen}
          onClose={handleCancelDeleteDataset}
          onConfirm={handleConfirmDeleteDataset}
          title={t("Confirm Dataset Deletion")}
          contentText={t("Are you sure you want to delete the dataset named") + datasets?.find(dataset => dataset.id === datasetToDelete)?.name}
        />

        

        <Paper style={{ padding: "1px" }}>
          {/* <Box {...getRootProps()} border="1px dashed grey" p={2} textAlign="center" style={{ cursor: 'pointer', width: '50%', marginLeft: "25%", marginTop: "20px", marginBottom: "20px" }}> */}
          <Box display="flex" alignItems="space-between" justifyContent="space-between">
            <Box {...getRootProps()} display="flex" alignItems="center" justifyContent="center" border="1px dashed grey" p={2} textAlign="center" style={{ cursor: 'pointer', width: '30%', height: '100px', marginLeft: "20px", marginTop: "20px", marginBottom: "20px" }}>
              <input {...getInputProps()} />
              <Typography variant="body1">{t("Drag & drop some files here, or click to select files")}</Typography>
            </Box>
            <Box>
              <Box display="flex" alignItems="center" justifyContent={"flex-end"} mr={2} mt={1}>
                <Typography variant="body2">{t("Show file infos")}</Typography>
                <ColoredSwitch checked={showFileInfos} onChange={toggleShowFileInfos} />
                <Box style={{minWidth: "80px"}}></Box>
                <Typography variant="body2">{t("Use deep backtest")}</Typography>
                <ColoredSwitch checked={datasets?.find(dataset => dataset.id === activeDatasetId)?.useDeepBacktest} onChange={handleUseDeepBacktestChange} />
              </Box>
              <Box display="flex" alignItems="center" justifyContent={"flex-end"} mr={3} mt={1}>
                {/* Switch to select whether to use deep backtest or to show date pickers */}
                {datasets?.find(dataset => dataset.id === activeDatasetId)?.useDeepBacktest && (
                  <><div>
                    <Typography variant="body1">{t("Start Date")}: </Typography>
                    <Box display="flex" alignItems="center" ml={2} mr={8}>
                      <DatePicker
                        value={datasets?.find(dataset => dataset.id === activeDatasetId)?.customStartDate ? dayjs(datasets?.find(dataset => dataset.id === activeDatasetId)?.customStartDate) : null}
                        onChange={handleStartDateChange}
                        format={getDatePickerFormat()}
                        maxDate={getMaxDate()}
                        slotProps={{ textField: { size: 'small' } }}                        
                      />
                      <IconButton onClick={handleClearStartDate}>
                        <ClearIcon />
                      </IconButton>
                    </Box>
                  </div>
                  <div>
                    <Typography variant="body1">{t("End Date")}: </Typography>
                    <Box display="flex" alignItems="center" ml={2}>
                      <DatePicker
                        value={datasets?.find(dataset => dataset.id === activeDatasetId)?.customEndDate ? dayjs(datasets?.find(dataset => dataset.id === activeDatasetId)?.customEndDate) : null}
                        onChange={handleEndDateChange}
                        format={getDatePickerFormat()}
                        maxDate={getMaxDate()}
                        slotProps={{ textField: { size: 'small' } }}
                      />
                      <IconButton onClick={handleClearEndDate}>
                        <ClearIcon />
                      </IconButton>
                    </Box>
                  </div>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper style={{ opacity: datasets?.find(dataset => dataset.id === activeDatasetId)?.include ? 1 : 0.5 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ padding: '8px' }}></TableCell>
                <TableCell style={{ padding: '8px' }}></TableCell>
                <TableCell style={{ cursor: 'pointer', fontSize: tableFontSize, padding: '8px' }}><strong>{t("Name")}</strong></TableCell>
                <TableCell style={{ width: "100%", fontSize: tableFontSize, padding: '8px' }}></TableCell>
                <TableCell style={{ fontSize: tableFontSize, minWidth: 150, textAlign: 'right', padding: '8px' }}><strong>{t("Initial Equity")}</strong></TableCell>
                <TableCell style={{ fontSize: tableFontSize, textAlign: 'center', padding: '8px' }}><strong>{t("Config")}</strong></TableCell>
                <TableCell style={{ padding: '8px' }}></TableCell>
                <TableCell style={{ padding: '8px' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataset.files.length > 0 ? (
                dataset.files.map((file, index) => (
                  <React.Fragment key={file.customId}>
                    <TableRow>
                      <TableCell style={{ fontSize: tableFontSize, padding: '8px' }}>
                        <IconButton onClick={() => moveFile(index, 'up')} disabled={index === 0}>
                          <ArrowUpwardIcon />
                        </IconButton>
                        <IconButton onClick={() => moveFile(index, 'down')} disabled={index === dataset.files.length - 1}>
                          <ArrowDownwardIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell style={{ fontSize: tableFontSize, padding: '8px' }}>
                        <ColoredSwitch 
                          checked={file.include} 
                          onChange={() => handleIncludeChange(file.customId)}
                        />
                      </TableCell>
                      <TableCell style={{ fontSize: tableFontSize, padding: '8px' }}>
                        {editingId === file.customId ? (
                          <Box display="flex" alignItems="center" width="100%">
                            <TextField 
                              value={newId} 
                              onChange={(event) => setNewId(event.target.value.trim())} 
                              onKeyDown={(event) => event.key === 'Enter' && handleSaveClick(file.customId)}
                              error={!!error}
                              helperText={error}
                              style={{ flex: 1, minWidth: 450 }}
                            />
                          </Box>
                        ) : (
                          <Box display="flex" flexDirection="column" alignItems="flex-start" style={{ minWidth: 300 }}>
                            <Typography>{file.customId}</Typography>
                            {showFileInfos && (
                              <Table size="small" style={{ marginTop: '8px', fontSize: '0.8rem', color: 'gray' }}>
                                <TableBody>
                                  <TableRow>
                                    <TableCell><strong>{t("File Name")}:</strong></TableCell>
                                    <TableCell>{file.name}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell><strong>{t("Recalculated Initial Equity")}:</strong></TableCell>
                                    <TableCell>{formatValue(recalculatedAmounts[file.customId], 2)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell><strong>{t("First Trade")}:</strong></TableCell>
                                    <TableCell>{new Date(file.parsedData[0]?.datetime).toLocaleDateString(getLocale())}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell><strong>{t("Last Trade")}:</strong></TableCell>
                                    <TableCell>{new Date(file.parsedData[file.parsedData.length - 1]?.datetime).toLocaleDateString(getLocale())}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell><strong>{t("Number of Trades")}:</strong></TableCell>
                                    <TableCell>{Math.abs(file.parsedData.length / 2)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell><strong>{t("Config File Name")}:</strong></TableCell>
                                    <TableCell>{file.configFileName || t("No config file")}</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell style={{ width: "100%", fontSize: tableFontSize, padding: '8px' }}></TableCell>
                      <TableCell style={{ fontSize: tableFontSize, minWidth: 150, textAlign: 'right', padding: '8px' }}>
                        {editingId === file.customId ? (
                          <TextField 
                            type="number"
                            value={newInitialCapital !== null ? newInitialCapital : file.initialCapital}
                            onChange={(event) => {
                              setNewInitialCapital(parseFloat(event.target.value));
                            }}
                            onKeyDown={(event) => event.key === 'Enter' && handleSaveClick(file.customId)}
                            style={{ minWidth: 140 }}
                          />
                        ) : (
                          formatValue(file.initialCapital, 2)
                        )}
                      </TableCell>
                      <TableCell style={{ fontSize: tableFontSize, textAlign: 'center', padding: '8px' }}>
                        {file.parsedConfig ? (
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <IconButton onClick={() => file.parsedConfig && handleViewConfig(file.parsedConfig)}>
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton onClick={() => handleDeleteConfig(file.customId)}>
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box textAlign="center" style={{ cursor: 'pointer' }}>
                            <input
                              type="file"
                              accept=".csv"
                              style={{ display: 'none' }}
                              onChange={handleConfigFileChange}
                              id={`config-upload-${file.customId}`}
                            />
                            <label htmlFor={`config-upload-${file.customId}`}>
                              <IconButton component="span" onClick={() => setConfigParentFileId(file.customId)}>
                                <UploadFileIcon />
                              </IconButton>
                            </label>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell style={{ fontSize: tableFontSize, padding: '8px' }}>
                        {editingId === file.customId ? (
                          <>
                            <IconButton onClick={() => handleSaveClick(file.customId)}>
                              <SaveIcon style={{ color: '#089981'}}/>
                            </IconButton>
                            <IconButton onClick={handleCancelClick}>
                              <CancelIcon style={{ color: 'lightcoral'}} />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton onClick={() => handleEditClick(file.customId, file.customId, file.initialCapital)}>
                            <EditIcon />
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell style={{ fontSize: tableFontSize, padding: '8px' }}>
                        <Button onClick={() => handleRemoveFile(file.customId)}>
                          <DeleteIcon />
                        </Button>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" style={{ fontSize: tableFontSize, padding: '8px' }}>
                    {t("No files uploaded yet.")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Box mb={2} mt={4}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell><Typography variant="h6">{t("All files")}</Typography></TableCell>
                <TableCell><Typography variant="h6">{t("Included files")}</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{t("First trade")}</TableCell>
                <TableCell>{allFilesStats.firstTradeDate?.toLocaleDateString(getLocale(), dateOptions) || t("-")}</TableCell>
                <TableCell>{includedFilesStats.firstTradeDate?.toLocaleDateString(getLocale(), dateOptions) || t("-")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t("Last Trade")}</TableCell>
                <TableCell>{allFilesStats.lastTradeDate?.toLocaleDateString(getLocale(), dateOptions) || t("-")}</TableCell>
                <TableCell>{includedFilesStats.lastTradeDate?.toLocaleDateString(getLocale(), dateOptions) || t("-")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t("Number of trades")}</TableCell>
                <TableCell>{allFilesStats.tradeCount}</TableCell>
                <TableCell>{includedFilesStats.tradeCount}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t("Number of Files")}</TableCell>
                <TableCell>{dataset.files.length}</TableCell>
                <TableCell>{dataset.files.filter(file => file.include).length}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </>
  );
};

export default ManageDataPage;
